"use client";

import { useState } from "react";
import { useNavigate, useLocation, Link as RouterLink, Navigate } from "react-router-dom";
import { showLoading } from "react-global-loading";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  MarkEmailRead as MarkEmailReadIcon,
  Login,
  Send as SendIcon,
  Mail,
} from "@mui/icons-material";

import * as myApi from "../../Api";
import { toast } from "react-toastify";
import Logo from "../../Logo";

const ConfirmEmailForm = ({ emailFromUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;

  const [activationCode, setActivationCode] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);

  // Check if user is already logged in but inactive
  const user = myApi.getUser();
  const token = myApi.getToken();
  const isLoggedIn = token && user && user.user_status !== "active";
  
  // Use email from URL parameter, or fallback to user data/location state
  const userEmail = emailFromUrl || user?.email || data?.email || "";

  // If no email available, redirect to login
  if (!userEmail && !isLoggedIn) {
    return <Navigate to="/auth/sign-in/" replace />;
  }

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setActivationCode(value);
    setButtonDisabled(value === "");
  };

  const handleOpenResendDialog = () => {
    setResendDialogOpen(true);
  };

  const handleCloseResendDialog = () => {
    setResendDialogOpen(false);
  };

  const handleResendCode = async () => {
    setResendDialogOpen(false);
    setResendLoading(true);
    try {
      const response = await myApi.post("/sendcode", {
        data_email: userEmail,
        data_kode: "sendcode",
      });
      
      if (response.data.status === "Success") {
        toast.success("Activation code has been resent to your email!", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (response.status === 200) {
        toast.success("Activation code has been resent to your email!", {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(response.data.message || "Failed to resend code", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error resending activation code", {
        position: "top-right",
        autoClose: 5000,
      });
      console.error(error);
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = () => {
    myApi.removeToken();
    myApi.removeUser();
    toast("Logged out successfully", { type: "info" });
    setTimeout(() => {
      navigate("/auth/sign-in/");
    }, 1000);
  };

  const emailActivation = (event) => {
    event.preventDefault();
    
    // Validate activation code is entered
    if (!activationCode || activationCode.trim() === "") {
      toast.error("Please enter your activation code", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    showLoading(true);

    // Use email from URL parameter first, then location state, then user data
    const verifyEmail = emailFromUrl || data?.email || user?.email || "";
    const verifyName = data?.name || user?.name || "";

    // If no email available, show error
    if (!verifyEmail) {
      showLoading(false);
      toast.error("Email not found. Please register again or use the activation link from your email.", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    const verifyData = {
      name: verifyName, // Name is optional, backend will work with just email + code
      email: verifyEmail,
      kode_registrasi: activationCode,
    };

    myApi.post("/verify", verifyData).then((response) => {
      showLoading(false);
      if (response.data.status === "OK" || response.status === 200) {
        // Activation successful
        if (isLoggedIn) {
          // For logged-in users: Update user status and redirect to their dashboard
          const updatedUser = myApi.getUser();
          if (updatedUser) {
            updatedUser.user_status = "active";
            myApi.setUser(updatedUser);
          }

          toast.success("Your account has been activated successfully! Redirecting...", {
            position: "top-right",
            autoClose: 3000,
          });

          setTimeout(() => {
            // Redirect based on user role
            if (updatedUser?.role === "admin") {
              navigate(myApi.dashboardPath);
            } else {
              navigate("/paper/list/");
            }
          }, 1500);
        } else {
          // For non-logged-in users: Redirect to login
          toast.success("Your account has been activated successfully! Please login.", {
            position: "top-right",
            autoClose: 3000,
          });

          setTimeout(() => {
            navigate(myApi.loginPath);
          }, 1500);
        }
      } else if (response.data.status === "Failed") {
        toast.warning(response.data.message || "Verification failed. Please check your activation code and try again.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(response.data.message || "Verification failed. Please check your activation code and try again.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }).catch((error) => {
      showLoading(false);
      const errorMessage = error.response?.data?.message || error.message || "Activation failed";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Activation error:", error);
    });
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Show warning if user is logged in but inactive */}
        {isLoggedIn && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Account Not Activated</AlertTitle>
            You are logged in as <strong>{userEmail}</strong> but your account is not yet activated.
            Please enter the activation code from your email.
          </Alert>
        )}

        <form onSubmit={emailActivation}>
          <Stack spacing={3} alignItems="center">
            {/* Logo */}
            <Box sx={{ mb: 2 }}>
              <Logo size={100} type="horizontal" />
            </Box>

            {/* Email Icon */}
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <MarkEmailReadIcon sx={{ fontSize: 80, color: "primary.main", opacity: 0.8 }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" component="h1" fontWeight={600}>
              {isLoggedIn ? "Activate Your Account" : "Success!"}
            </Typography>

            {/* Description */}
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {isLoggedIn ? (
                <>
                  An activation email was sent to <strong>{userEmail}</strong>.<br />
                  Please check your inbox and enter the activation code below.
                </>
              ) : (
                <>
                  A verification email has been sent to <strong>{emailFromUrl || data?.email}</strong>.<br />
                  Please check your inbox and paste the activation code below.
                </>
              )}
            </Typography>

            {/* Spam folder warning */}
            <Alert severity="info" sx={{ width: '100%' }}>
              <AlertTitle>Check Your Inbox or Spam Folder</AlertTitle>
              If you don't see the email in your inbox, please check your <strong>spam</strong> or <strong>junk</strong> folder. 
              Our email may be filtered by your email provider.
            </Alert>

            {/* Activation Code Input */}
            <TextField
              fullWidth
              label="Activation Code"
              name="kode_registrasi"
              value={activationCode}
              onChange={handleCodeChange}
              placeholder="Enter your activation code here"
              required
              variant="outlined"
              sx={{
                '& input': {
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  letterSpacing: '2px',
                  fontWeight: 600,
                },
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={buttonDisabled}
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Activate My Account
            </Button>

            {/* Navigation Links */}
            <Stack direction="row" spacing={2} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap" justifyContent="center">
              {/* Show "Back to Sign In" only when user is NOT logged in */}
              {!isLoggedIn && (
                <Link
                  component={RouterLink}
                  to="/auth/sign-in/"
                  underline="hover"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontWeight: 500,
                  }}
                >
                  <Login sx={{ fontSize: 18 }} />
                  Back to Sign In
                </Link>
              )}

              {/* Show resend code link for all users */}
              <Link
                component="button"
                type="button"
                onClick={handleOpenResendDialog}
                underline="hover"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontWeight: 500,
                  color: "primary.main",
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  "&:hover": {
                    color: "primary.dark",
                  },
                }}
              >
                <Mail sx={{ fontSize: 18 }} />
                Resend Activation Code
              </Link>

              {/* Show logout button only if user is logged in */}
              {isLoggedIn && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleLogout}
                  sx={{ textTransform: "none" }}
                >
                  Logout
                </Button>
              )}
            </Stack>
          </Stack>
        </form>

        {/* Resend Code Confirmation Dialog */}
        <Dialog
          open={resendDialogOpen}
          onClose={handleCloseResendDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            },
          }}
        >
          <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center" }}>
            <Box sx={{ mb: 2 }}>
              <Mail sx={{ color: "primary.main", fontSize: 48 }} />
            </Box>
            <Typography variant="h5" fontWeight={600}>
              Resend Activation Code?
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pb: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              We will send a new activation code to <strong>{userEmail}</strong>.
              <br />
              Are you sure you want to continue?
            </Typography>
            {resendLoading && (
              <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
                Sending activation code...
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 2, justifyContent: "center" }}>
            <Button
              onClick={handleCloseResendDialog}
              variant="outlined"
              size="large"
              fullWidth
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResendCode}
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={resendLoading}
              startIcon={resendLoading ? null : <SendIcon />}
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {resendLoading ? "Sending..." : "Send Code"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © 2026 KARSA NUSA. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConfirmEmailForm;
